import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError, type components } from '@akillidernek/api-client'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Input,
  useToast,
} from '@akillidernek/ui'
import { api } from '../api'
import { tenantSlug } from '../tenant'

type Talep = components['schemas']['PortalTicketRead']

const DURUM: Record<
  Talep['status'],
  { etiket: string; ton: 'info' | 'warning' | 'success' | 'neutral' }
> = {
  open: { etiket: 'Açık', ton: 'info' },
  waiting_requester: { etiket: 'Yanıtınız bekleniyor', ton: 'warning' },
  resolved: { etiket: 'Çözüldü', ton: 'success' },
  closed: { etiket: 'Kapalı', ton: 'neutral' },
}

const zaman = (iso: string) => new Date(iso).toLocaleString('tr-TR')

/**
 * Üyenin destek talepleri — YES-167.
 *
 * Yalnızca kendi talepleri; sunucu da bunu ayrıca kısıtlıyor. İç notlar bu
 * ekranın veri modelinde yok: sızabileceği bir yer bırakılmadı.
 */
export function Destek() {
  const [acikTalep, setAcikTalep] = useState<Talep | null>(null)
  const [yeni, setYeni] = useState(false)
  const toast = useToast()
  const queryClient = useQueryClient()

  const talepler = useQuery({
    queryKey: ['portal-tickets', tenantSlug],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/portal/tickets')
      return data ?? []
    },
  })

  const ac = useMutation({
    mutationFn: async (form: FormData) => {
      const { data } = await api.POST('/api/v1/portal/tickets', {
        body: {
          subject: String(form.get('subject') ?? '').trim(),
          body: String(form.get('body') ?? '').trim(),
        },
      })
      return data
    },
    onSuccess: (data) => {
      setYeni(false)
      toast({ title: 'Talebiniz iletildi', tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['portal-tickets', tenantSlug] })
      if (data) setAcikTalep(data)
    },
    onError: (error) =>
      toast({
        title: 'Talep açılamadı',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title="Destek talepleri"
          description="Derneğe sorunuzu iletin, yanıtları buradan takip edin."
          action={
            <Button size="sm" onClick={() => setYeni(true)}>
              Talep aç
            </Button>
          }
        />
        <CardBody>
          {talepler.data && talepler.data.length === 0 ? (
            <EmptyState
              title="Henüz talebiniz yok"
              description="Aidat, makbuz ya da üyelikle ilgili sorularınızı buradan sorabilirsiniz."
            />
          ) : (
            <ul className="flex flex-col divide-y divide-[var(--ad-border)]">
              {(talepler.data ?? []).map((talep) => (
                <li key={talep.number}>
                  <button
                    type="button"
                    onClick={() => setAcikTalep(talep)}
                    className="flex w-full items-center gap-3 py-3 text-left text-sm hover:bg-[var(--ad-surface-muted)]"
                  >
                    <span className="w-10 shrink-0 font-mono text-xs text-[var(--ad-text-muted)]">
                      #{talep.number}
                    </span>
                    <span className="min-w-0 flex-1 font-medium">{talep.subject}</span>
                    <Badge tone={DURUM[talep.status].ton}>{DURUM[talep.status].etiket}</Badge>
                    <span className="hidden w-40 text-xs text-[var(--ad-text-muted)] sm:block">
                      {zaman(talep.last_message_at ?? talep.created_at)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {acikTalep && <TalepDetay talep={acikTalep} onKapat={() => setAcikTalep(null)} />}

      <Dialog
        open={yeni}
        onOpenChange={setYeni}
        title="Yeni talep"
        description="Konuyu kısaca yazın; dernek görevlisi yanıtlayacak."
        footer={
          <>
            <Button variant="secondary" onClick={() => setYeni(false)}>
              Vazgeç
            </Button>
            <Button type="submit" form="talep-formu" disabled={ac.isPending}>
              {ac.isPending ? 'Gönderiliyor…' : 'Gönder'}
            </Button>
          </>
        }
      >
        <form
          id="talep-formu"
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            ac.mutate(new FormData(event.currentTarget))
          }}
        >
          <Field label="Konu" required>
            {(props) => <Input name="subject" placeholder="Aidat makbuzum gelmedi" {...props} />}
          </Field>
          <Field label="Mesaj" required>
            {({ id, invalid }) => (
              <textarea
                id={id}
                name="body"
                required
                rows={5}
                aria-invalid={invalid || undefined}
                className="w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] p-2 text-sm"
              />
            )}
          </Field>
        </form>
      </Dialog>
    </div>
  )
}

function TalepDetay({ talep, onKapat }: { talep: Talep; onKapat: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const mesajlar = useQuery({
    queryKey: ['portal-ticket', tenantSlug, talep.number],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/portal/tickets/{number}/messages', {
        params: { path: { number: talep.number } },
      })
      return data ?? []
    },
  })

  const yaz = useMutation({
    mutationFn: async (form: FormData) => {
      await api.POST('/api/v1/portal/tickets/{number}/messages', {
        params: { path: { number: talep.number } },
        body: { body: String(form.get('body') ?? '').trim() },
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['portal-ticket', tenantSlug, talep.number] })
      void queryClient.invalidateQueries({ queryKey: ['portal-tickets', tenantSlug] })
    },
    onError: (error) =>
      toast({
        title: 'Gönderilemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  const kapali = talep.status === 'closed'

  return (
    <Dialog
      open
      onOpenChange={(acik) => {
        if (!acik) onKapat()
      }}
      title={`#${talep.number} · ${talep.subject}`}
      description={DURUM[talep.status].etiket}
      footer={
        <Button variant="secondary" onClick={onKapat}>
          Kapat
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <ol className="flex max-h-80 flex-col gap-2 overflow-y-auto">
          {(mesajlar.data ?? []).map((mesaj) => (
            <li
              key={mesaj.id}
              className={
                mesaj.from_requester
                  ? 'self-end rounded-[var(--radius-control)] bg-[var(--ad-primary)]/12 px-3 py-2 text-sm'
                  : 'self-start rounded-[var(--radius-control)] bg-[var(--ad-surface-muted)] px-3 py-2 text-sm'
              }
            >
              <p className="whitespace-pre-wrap">{mesaj.body}</p>
              <p className="pt-1 text-xs text-[var(--ad-text-muted)]">
                {mesaj.from_requester ? 'Siz' : 'Dernek'} · {zaman(mesaj.created_at)}
              </p>
            </li>
          ))}
        </ol>

        {kapali ? (
          <p className="text-xs text-[var(--ad-text-muted)]">
            Bu talep kapatıldı. Yeni bir sorunuz varsa yeni talep açın.
          </p>
        ) : (
          <form
            className="flex flex-col gap-2"
            onSubmit={(event) => {
              event.preventDefault()
              const form = new FormData(event.currentTarget)
              event.currentTarget.reset()
              yaz.mutate(form)
            }}
          >
            <Field label="Yanıtınız" required>
              {({ id, invalid }) => (
                <textarea
                  id={id}
                  name="body"
                  required
                  rows={3}
                  aria-invalid={invalid || undefined}
                  className="w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] p-2 text-sm"
                />
              )}
            </Field>
            <Button type="submit" size="sm" disabled={yaz.isPending}>
              {yaz.isPending ? 'Gönderiliyor…' : 'Gönder'}
            </Button>
          </form>
        )}
      </div>
    </Dialog>
  )
}
